import React, { useState } from "react";
import { UserPlus, Send, CheckCircle2, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const GROUPS = [
  "Jeunesse",
  "Louange & Musique",
  "Prière & Intercession",
  "Étude biblique",
  "Enfants",
  "Diaconat / Service",
  "Évangélisation",
  "Accueil",
];

const EMPTY = {
  first_name: "", last_name: "", email: "", phone: "",
  birth_date: "", gender: "", city: "", address: "",
  marital_status: "", how_did_you_hear: "",
  interested_groups: [], prayer_request: "",
  is_new_convert: false, accepted_terms: false,
};

export default function RegistrationSection() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k) => (v) => {
    const val = v?.target ? v.target.value : v;
    setForm((f) => ({ ...f, [k]: val }));
  };

  const toggleGroup = (g) => {
    setForm((f) => ({
      ...f,
      interested_groups: f.interested_groups.includes(g)
        ? f.interested_groups.filter((x) => x !== g)
        : [...f.interested_groups, g],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.accepted_terms) {
      toast.error("Veuillez accepter les conditions pour continuer");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      // Clean empty optional strings
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      payload.first_name = form.first_name;
      payload.last_name = form.last_name;
      payload.email = form.email;
      payload.phone = form.phone;
      payload.interested_groups = form.interested_groups;
      payload.is_new_convert = form.is_new_convert;
      payload.accepted_terms = form.accepted_terms;

      await api.post("/registrations", payload);
      setDone(true);
      toast.success("Inscription enregistrée 🙏", {
        description: "Bienvenue dans la famille !",
      });
      window.scrollTo({ top: document.getElementById("inscription").offsetTop - 80, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <section id="inscription" className="py-24 lg:py-32 bg-gradient-to-br from-white via-blue-50 to-white">
        <div className="max-w-2xl mx-auto px-4 text-center" data-testid="registration-success">
          <div className="w-20 h-20 mx-auto rounded-full bg-gold/20 flex items-center justify-center mb-6">
            <CheckCircle2 className="w-10 h-10 text-gold" />
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl text-deepblue-900">Bienvenue chez nous 🙏</h2>
          <p className="mt-4 text-lg text-slate-600">
            Merci pour votre inscription. Un email de confirmation vous a été envoyé. Un membre de notre équipe
            vous contactera prochainement pour vous accueillir personnellement.
          </p>
          <Button
            onClick={() => { setForm(EMPTY); setDone(false); }}
            className="mt-8 bg-gold hover:bg-gold-hover text-white rounded-full px-8"
            data-testid="new-registration-btn"
          >
            Nouvelle inscription
          </Button>
        </div>
      </section>
    );
  }

  return (
    <section id="inscription" className="py-24 lg:py-32 bg-gradient-to-br from-white via-blue-50 to-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 text-gold-dark text-xs tracking-[0.3em] uppercase mb-4">
            <UserPlus className="w-3.5 h-3.5" /> Inscription
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-deepblue-900 font-semibold leading-tight">
            Rejoindre notre communauté
          </h2>
          <p className="mt-4 text-slate-600 text-lg">
            Remplissez ce formulaire pour vous inscrire. Nous vous contacterons rapidement pour vous accueillir.
          </p>
        </div>

        <form onSubmit={submit} className="bg-white rounded-3xl shadow-xl border border-blue-100 p-8 sm:p-10 space-y-8" data-testid="registration-form">
          {/* Identity */}
          <fieldset className="space-y-5">
            <legend className="font-serif text-xl text-deepblue-900 mb-2">Vos coordonnées</legend>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Prénom" required>
                <Input required value={form.first_name} onChange={upd("first_name")} data-testid="reg-first-name" />
              </Field>
              <Field label="Nom" required>
                <Input required value={form.last_name} onChange={upd("last_name")} data-testid="reg-last-name" />
              </Field>
              <Field label="Email" required>
                <Input type="email" required value={form.email} onChange={upd("email")} data-testid="reg-email"
                  placeholder="votre.email@exemple.com" />
              </Field>
              <Field label="Téléphone / WhatsApp" required>
                <Input required value={form.phone} onChange={upd("phone")} data-testid="reg-phone"
                  placeholder="+33 6 12 34 56 78" />
              </Field>
              <Field label="Date de naissance">
                <Input type="date" value={form.birth_date} onChange={upd("birth_date")} data-testid="reg-birth-date" />
              </Field>
              <Field label="Sexe">
                <Select value={form.gender} onValueChange={upd("gender")}>
                  <SelectTrigger data-testid="reg-gender"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M">Masculin</SelectItem>
                    <SelectItem value="F">Féminin</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Ville">
                <Input value={form.city} onChange={upd("city")} data-testid="reg-city" placeholder="Paris" />
              </Field>
              <Field label="Statut familial">
                <Select value={form.marital_status} onValueChange={upd("marital_status")}>
                  <SelectTrigger data-testid="reg-marital"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="celibataire">Célibataire</SelectItem>
                    <SelectItem value="marie">Marié(e)</SelectItem>
                    <SelectItem value="en_couple">En couple</SelectItem>
                    <SelectItem value="divorce">Divorcé(e)</SelectItem>
                    <SelectItem value="veuf">Veuf(ve)</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Adresse complète">
              <Input value={form.address} onChange={upd("address")} data-testid="reg-address"
                placeholder="Numéro, rue, code postal" />
            </Field>
          </fieldset>

          {/* Spiritual */}
          <fieldset className="space-y-5 pt-4 border-t border-blue-100">
            <legend className="font-serif text-xl text-deepblue-900 mb-2">Votre parcours spirituel</legend>
            <Field label="Comment avez-vous connu notre église ?">
              <Select value={form.how_did_you_hear} onValueChange={upd("how_did_you_hear")}>
                <SelectTrigger data-testid="reg-how"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="amis">Un ami / famille</SelectItem>
                  <SelectItem value="reseaux">Réseaux sociaux</SelectItem>
                  <SelectItem value="youtube">YouTube / vidéos</SelectItem>
                  <SelectItem value="recherche">Recherche internet</SelectItem>
                  <SelectItem value="passage">De passage</SelectItem>
                  <SelectItem value="evenement">Événement</SelectItem>
                  <SelectItem value="autre">Autre</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            <div>
              <Label>Groupes / ministères qui vous intéressent</Label>
              <div className="mt-3 grid sm:grid-cols-2 gap-2" data-testid="reg-groups">
                {GROUPS.map((g) => (
                  <label key={g} className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border cursor-pointer transition-colors ${
                    form.interested_groups.includes(g) ? "bg-deepblue-900 text-white border-deepblue-900" : "bg-white border-slate-200 hover:border-deepblue-600"
                  }`}>
                    <Checkbox
                      checked={form.interested_groups.includes(g)}
                      onCheckedChange={() => toggleGroup(g)}
                      className="border-current data-[state=checked]:bg-gold data-[state=checked]:border-gold"
                    />
                    <span className="text-sm">{g}</span>
                  </label>
                ))}
              </div>
            </div>

            <label className="flex items-start gap-3 p-4 rounded-lg bg-blue-50 border border-blue-200 cursor-pointer">
              <Checkbox
                checked={form.is_new_convert}
                onCheckedChange={(v) => upd("is_new_convert")(!!v)}
                data-testid="reg-new-convert"
                className="mt-0.5"
              />
              <div className="text-sm">
                <div className="font-semibold text-deepblue-900">Je viens de donner ma vie à Jésus-Christ ✝️</div>
                <div className="text-slate-600">Cochez si vous êtes un nouveau converti. Nous vous accompagnerons dans vos premiers pas.</div>
              </div>
            </label>

            <Field label="Demande de prière (facultatif)">
              <Textarea rows={3} value={form.prayer_request} onChange={upd("prayer_request")}
                data-testid="reg-prayer" placeholder="Partagez ce qui pèse sur votre cœur…" />
            </Field>
          </fieldset>

          {/* Consent */}
          <div className="pt-4 border-t border-blue-100">
            <label className="flex items-start gap-3 cursor-pointer">
              <Checkbox
                checked={form.accepted_terms}
                onCheckedChange={(v) => upd("accepted_terms")(!!v)}
                required
                data-testid="reg-terms"
                className="mt-0.5"
              />
              <span className="text-sm text-slate-600">
                J'accepte que mes données soient utilisées uniquement pour être contacté par l'équipe pastorale
                dans le cadre de mon accueil dans l'église. Mes données ne seront jamais transmises à des tiers.
              </span>
            </label>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-4 pt-2">
            <Button type="submit" disabled={submitting || !form.accepted_terms}
              className="bg-gold hover:bg-gold-hover text-white rounded-full px-10 py-6 text-base w-full sm:w-auto"
              data-testid="reg-submit">
              <Send className="w-4 h-4 mr-2" />
              {submitting ? "Envoi en cours…" : "M'inscrire"}
            </Button>
            <div className="text-sm text-slate-500 flex items-center gap-2">
              <Heart className="w-4 h-4 text-gold" /> Vos données restent confidentielles
            </div>
          </div>
        </form>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-slate-700">{label}{required && <span className="text-red-500 ml-1">*</span>}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
